var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import clsx from 'clsx';
import { KeyAttributeValue, mergeProps, useClasses, useControlledStateWarning, useLayoutProps, useProps, } from '@hawkins/helpers';
import { IconCaretDown } from '@hawkins/icons/lib/iconCaretDown';
import { IconCaretUp } from '@hawkins/icons/lib/iconCaretUp';
import { IconClose } from '@hawkins/icons/lib/iconClose';
import { IconTime } from '@hawkins/icons/lib/iconTime';
import { Button } from '../button';
import { Card } from '../card';
import { FloatingUI } from '../floatingUi';
import { useCombineRefs } from '../hooks';
import { Input } from '../input';
import { TimeMaskedInput } from './timeMaskedInput';
import { TimePicker } from './timePicker';
import { DEFAULT_TIME_SEGMENT_DELIMITER, DEFAULT_TIME_STRING_SEGMENT_DELIMITER, defaultFormatTime, defaultParseTime, } from './timePicker.utils';
import { useTimePickerInputStyles } from './timePickerInput.styles';
import { TimePickerInputTestIds } from './timePickerInput.testids';
var displayName = 'TimePickerInput';
/**
 * `<TimePickerInput>` renders a `<TimePicker>` in the context of an input field,
 * allowing the user to type in a value.
 */
export var TimePickerInput = React.forwardRef(function (props, ref) {
    var _a;
    // Props
    var _b = useProps(displayName, props), _c = _b.cardProps, _d = _c === void 0 ? {} : _c, _e = _d.ref, cardRefProp = _e === void 0 ? undefined : _e, cardProps = __rest(_d, ["ref"]), classesProp = _b.classes, className = _b.className, _f = _b.clearable, clearable = _f === void 0 ? false : _f, _g = _b.component, Component = _g === void 0 ? 'div' : _g, dateProp = _b.date, _h = _b.defaultDate, defaultDate = _h === void 0 ? null : _h, _j = _b.defaultOpen, defaultOpen = _j === void 0 ? false : _j, _k = _b.disablePortal, disablePortal = _k === void 0 ? true : _k, _l = _b.disabled, disabled = _l === void 0 ? false : _l, inputComponent = _b.inputComponent, _m = _b.inputProps, _o = _m === void 0 ? {} : _m, _p = _o.inputProps, inputInputPropsProp = _p === void 0 ? undefined : _p, _q = _o.inputRef, inputInputRefProp = _q === void 0 ? undefined : _q, _r = _o.onChange, inputOnChangeProp = _r === void 0 ? undefined : _r, _s = _o.ref, inputContainerRefProp = _s === void 0 ? undefined : _s, inputProps = __rest(_o, ["inputProps", "inputRef", "onChange", "ref"]), layout = _b.layout, onChange = _b.onChange, onInputValueChange = _b.onInputValueChange, onToggle = _b.onToggle, openProp = _b.open, parseTime = _b.parseTime, styleProp = _b.style, _t = _b.timePickerProps, timePickerProps = _t === void 0 ? {} : _t, label = _b.label, description = _b.description, restProps = __rest(_b, ["cardProps", "classes", "className", "clearable", "component", "date", "defaultDate", "defaultOpen", "disablePortal", "disabled", "inputComponent", "inputProps", "layout", "onChange", "onInputValueChange", "onToggle", "open", "parseTime", "style", "timePickerProps", "label", "description"]);
    useControlledStateWarning(dateProp, { displayName: displayName });
    var _u = timePickerProps.isUTC, isUTC = _u === void 0 ? false : _u, _v = timePickerProps.use12HourClock, use12HourClock = _v === void 0 ? false : _v, _w = timePickerProps.withSeconds, withSeconds = _w === void 0 ? false : _w;
    var isDateControlled = dateProp !== undefined;
    var isOpenControlled = openProp !== undefined;
    // Refs
    var internalCardRef = React.useRef(null);
    var internalInputContainerRef = React.useRef(null);
    var internalInputRef = React.useRef(null);
    var cardRef = useCombineRefs([internalCardRef, cardRefProp]);
    var inputContainerRef = useCombineRefs([internalInputContainerRef, inputContainerRefProp]);
    var inputRef = useCombineRefs([internalInputRef, inputInputRefProp]);
    var focusInput = React.useCallback(function () {
        var _a;
        (_a = internalInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, []);
    // State
    var _x = __read(React.useState(defaultFormatTime(defaultDate, { isUTC: isUTC, use12HourClock: use12HourClock, withSeconds: withSeconds })), 2), inputValue = _x[0], setInputValue = _x[1];
    var _y = __read(React.useState(defaultDate), 2), internalDate = _y[0], setInternalDate = _y[1];
    var _z = __read(React.useState(defaultOpen), 2), internalOpen = _z[0], setInternalOpen = _z[1];
    var derivedDate = isDateControlled ? dateProp : internalDate;
    var derivedOpen = isOpenControlled ? openProp : internalOpen;
    var EndAdornmentIcon = derivedOpen ? IconCaretUp : IconCaretDown;
    var toggleDropdown = React.useCallback(function (isOpen) {
        if (!isOpenControlled)
            setInternalOpen(isOpen);
        onToggle === null || onToggle === void 0 ? void 0 : onToggle(isOpen);
    }, [isOpenControlled, onToggle]);
    var openDropdown = React.useCallback(function () { return toggleDropdown(true); }, [toggleDropdown]);
    var handleBlur = React.useCallback(function (ev) {
        var _a, _b;
        // Don't close the dropdown if focus is staying within the component.
        if (ev.relatedTarget instanceof Element &&
            (((_a = internalCardRef.current) === null || _a === void 0 ? void 0 : _a.contains(ev.relatedTarget)) ||
                ((_b = internalInputContainerRef.current) === null || _b === void 0 ? void 0 : _b.contains(ev.relatedTarget)))) {
            return;
        }
        toggleDropdown(false);
    }, [toggleDropdown]);
    var handleChange = React.useCallback(function (selectedTime) {
        if (!isDateControlled)
            setInternalDate(selectedTime);
        onChange === null || onChange === void 0 ? void 0 : onChange(selectedTime);
    }, [isDateControlled, onChange]);
    var onCardKeyDown = React.useCallback(function (ev) {
        if (ev.key === KeyAttributeValue.ESCAPE || ev.key === KeyAttributeValue.ENTER) {
            toggleDropdown(false);
        }
    }, [toggleDropdown]);
    var onInputChange = React.useCallback(function (ev) {
        var value = ev.target.value;
        setInputValue(value);
        onInputValueChange === null || onInputValueChange === void 0 ? void 0 : onInputValueChange(value);
        var selectedTime = (parseTime || defaultParseTime)(value, { isUTC: isUTC, use12HourClock: use12HourClock, withSeconds: withSeconds });
        // Don't trigger `handleChange` for multiple null dates in a row
        // (if the user is typing into the input and the time string isn't valid yet).
        if ((derivedDate === null || derivedDate === void 0 ? void 0 : derivedDate.getTime()) !== (selectedTime === null || selectedTime === void 0 ? void 0 : selectedTime.getTime())) {
            handleChange(selectedTime);
        }
        inputOnChangeProp === null || inputOnChangeProp === void 0 ? void 0 : inputOnChangeProp(ev);
    }, [derivedDate, handleChange, inputOnChangeProp, isUTC, onInputValueChange, parseTime, use12HourClock, withSeconds]);
    var onInputKeyDown = React.useCallback(function (ev) {
        if (ev.key === KeyAttributeValue.ENTER) {
            var newOpen = !derivedOpen;
            if (!isOpenControlled)
                setInternalOpen(newOpen);
            onToggle === null || onToggle === void 0 ? void 0 : onToggle(newOpen);
        }
        else if (ev.key === KeyAttributeValue.ESCAPE || ev.key === KeyAttributeValue.TAB) {
            toggleDropdown(false);
        }
    }, [derivedOpen, isOpenControlled, onToggle, toggleDropdown]);
    // Update the input value when relevant variables change.
    React.useEffect(function () {
        // Allow the user to erase part of their input without
        // losing the entire input value due to the selected time being null.
        if (!derivedDate && !(parseTime || defaultParseTime)(inputValue, { isUTC: isUTC, use12HourClock: use12HourClock, withSeconds: withSeconds })) {
            return;
        }
        var newInputValue = defaultFormatTime(derivedDate, { isUTC: isUTC, use12HourClock: use12HourClock, withSeconds: withSeconds });
        if (newInputValue !== inputValue) {
            setInputValue(newInputValue);
            onInputValueChange === null || onInputValueChange === void 0 ? void 0 : onInputValueChange(newInputValue);
        }
        // Don't run this effect unnecessarily.
        //
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [derivedDate, isUTC, use12HourClock, withSeconds]);
    // Styles
    var baseClasses = useTimePickerInputStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var rootClassName = clsx(classes.HAWKINS_BASE, className, classes.root);
    var startAdornmentIconClassName = clsx(classes.startAdornmentIcon, (_a = {},
        _a[classes.startAdornmentIconDisabled] = disabled,
        _a));
    // callbacks
    var handleClear = React.useCallback(function (e) {
        e.stopPropagation();
        handleChange(null);
    }, [handleChange]);
    // renderable memos
    var endAdornment = React.useMemo(function () {
        return (_jsxs(_Fragment, { children: [clearable && inputValue && (_jsx(Button, { "aria-label": "Clear time", className: classes.clearBtn, icon: IconClose, onClick: handleClear, variant: "icon" })), _jsx(EndAdornmentIcon, { className: classes.endAdornmentIcon, size: "small" })] }));
    }, [EndAdornmentIcon, classes.clearBtn, classes.endAdornmentIcon, clearable, handleClear, inputValue]);
    // Content
    var cardCallbacks = {
        onBlur: handleBlur,
        onKeyDown: onCardKeyDown,
    };
    var inputCallbacks = {
        onBlur: handleBlur,
        onClick: focusInput,
        onFocus: openDropdown,
        onKeyDown: onInputKeyDown,
    };
    var inputInputProps = React.useMemo(function () { return (__assign(__assign({}, inputInputPropsProp), { use12HourClock: use12HourClock, withSeconds: withSeconds })); }, [inputInputPropsProp, use12HourClock, withSeconds]);
    return (_jsxs(Component, __assign({ className: rootClassName, "data-testid": TimePickerInputTestIds.ROOT, ref: ref, style: style }, restProps, { children: [_jsx(Input, __assign({ active: derivedOpen, autoComplete: "off", "data-testid": TimePickerInputTestIds.INPUT, endAdornment: endAdornment, inputProps: inputInputProps, inputRef: inputRef, onChange: onInputChange, placeholder: [
                    ['hh', 'mm', withSeconds && 'ss']
                        .filter(function (x) { return x; })
                        .join(DEFAULT_TIME_SEGMENT_DELIMITER)
                        .trim(),
                    use12HourClock && '__',
                ]
                    .filter(function (x) { return x; })
                    .join(DEFAULT_TIME_STRING_SEGMENT_DELIMITER)
                    .trim(), ref: inputContainerRef, startAdornment: _jsx(IconTime, { className: startAdornmentIconClassName, size: "small" }), value: inputValue, label: label, description: description }, mergeProps(inputCallbacks, inputProps), { disabled: disabled, inputComponent: inputComponent === undefined ? TimeMaskedInput : inputComponent || undefined })), _jsx(FloatingUI, __assign({ anchor: internalInputContainerRef.current, className: classes.floatingUI, disablePortal: disablePortal, open: derivedOpen, placement: "bottom-start", styled: false }, { children: _jsx(Card, __assign({ className: classes.card, elevation: 2, ref: cardRef, 
                    // Make the Card focusable (but not navigable by keyboard)
                    // to allow for detecting when a user clicks inside it.
                    tabIndex: -1 }, mergeProps(cardCallbacks, cardProps), { children: _jsx(TimePicker, __assign({}, timePickerProps, { date: derivedDate, disabled: disabled, onChange: handleChange })) })) }))] })));
});
TimePickerInput.displayName = displayName;
